import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import AcceptInvitation from '../components/accept-invitation-view';
import {
  eaIsLoading,
  extAdminMeRequestWithInvitationsGet,
  extAdminInvitationAccept,
  extAdminInvitationDecline,
  getInvitingAccount,
  getMyUserKey,
  getInvitingAccountKey,
  getInvitingAccountName,
  getConflictingAccountDetails,
  getInvitedLicenses,
  isInvitationValid,
  setInvitationId,
  getMyEmailAddress,
} from 'modules/extadmin-module';
import {
  stopEvent,
  scrollToTop,
} from 'lib/utility-functions';
import {
  uiShowConfirmDialog,
  uiShowToastrMessage,
  uiShowMessageBanner,
  uiShowMessageBannerToggle,
  uiMessageBannerVisible,
} from 'modules/ui-module';
import environment from 'lib/environment';
import appconfig from 'config/appconfig';
import {
  setHandledErrorCodes,
  removeHandledErrorCodes
} from 'lib/error-message-handling';
import auth from "lib/profile-auth";

const myAccountUrl = environment.get().myAccount_url;

export class AcceptInvitationContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      userInvitationType: appconfig.userInvitationType.UPDATED,
      isInvitationValid: false,
    };
  }

  componentDidMount() {
    if(this.props.invitationId) {
      this.props.setInvitationId(this.props.invitationId);
      this.props.extAdminMeRequestWithInvitationsGet();
    } else {
      this.setState({isInvitationValid: false});
    }
  }

  componentDidUpdate = (prevProps) => {
    if(this.props.isInvitationValid !== prevProps.isInvitationValid) {
      this.setState({isInvitationValid: this.props.isInvitationValid});
    }
  };

  displayToastrMessage = (toastrType, toastrMessageId, toastrParams = null) => {
    this.props.uiShowToastrMessage(toastrType, toastrMessageId, toastrParams);
  };

  redirectToMyAccount = () => {
    location.href = environment.get().myAccount_url;
  };

  getErrorCodesArray = () => {
    return [
      500,
      'invitation.not.found',
    ];
  };

  handleFailedActionResponse = (ex = {}) => {
    scrollToTop();
    if (ex && ex.status) {
      if (ex.status === 404) {
        this.props.uiShowMessageBanner('userInvitationExpired');
        this.setState({isInvitationValid: false});
      } else if (ex.status === 500) {
        this.props.uiShowMessageBanner('userInvitationError');
      }
    }
    removeHandledErrorCodes(this.getErrorCodesArray());
  }

  getParams = () => {
    return {
      accountKey: this.props.invitingAccountKey,
      userKey: this.props.myUserKey,
      invitationId: this.props.invitationId
    };
  };

  acceptInvitation = (e) => {
    stopEvent(e);

    setHandledErrorCodes(this.getErrorCodesArray());

    if(this.props.uiMessageBannerVisible) {
      this.props.uiShowMessageBannerToggle();
    }
    return this.props.extAdminInvitationAccept(this.getParams())
      .then(
        () => {
          this.props.uiShowConfirmDialog("accept", () => true, () => true, true, {
            invitingAccountName: this.props.invitingAccountName,
            myAccountUrl: myAccountUrl
          });
          removeHandledErrorCodes(this.getErrorCodesArray());
        },
        (ex) => {
          this.handleFailedActionResponse(ex);
        }
      );
  };

  showDeclineDialog = () => {
    this.props.uiShowConfirmDialog("decline", this.declineInvitation, () => true, false, {
      invitingAccountName: this.props.invitingAccountName
    });
  };

  declineInvitation = (e) => {
    stopEvent(e);

    setHandledErrorCodes(this.getErrorCodesArray());

    if(this.props.uiMessageBannerVisible) {
      this.props.uiShowMessageBannerToggle();
    }
    return this.props.extAdminInvitationDecline(this.getParams())
      .then(
        () => {
          removeHandledErrorCodes(this.getErrorCodesArray());
          this.redirectToMyAccount();
        },
        (ex) => {
          this.handleFailedActionResponse(ex);
        }
      );
  };

  getFirstProductNames = () => {
    if(!this.props.invitedLicenses || this.props.invitedLicenses.length < 1) {
      return '';
    }
    let firstProductNames = [];
    const tempProductArray = this.props.invitedLicenses.slice(0, -1);
    tempProductArray.forEach((product) => firstProductNames.push(product.description));
    return firstProductNames.join(', ');
  };

  getLastProductName = () => {
    if(!this.props.invitedLicenses || !this.props.invitedLicenses.length) {
      return '';
    }
    return this.props.invitedLicenses[this.props.invitedLicenses.length - 1].description;
  };

  getParsedAccountNames = (accountNamesArray) => {
    const parsedAccountNames = [];
    if(!accountNamesArray || !accountNamesArray.length) {
      return parsedAccountNames;
    }
    if(accountNamesArray.length === 1) {
      parsedAccountNames.push(accountNamesArray[0]);
    } else {
      parsedAccountNames[1] = accountNamesArray[accountNamesArray.length -1];
      const firstAccountNames = [];
      const tempAccountNamesArray = accountNamesArray.slice(0, -1);
      tempAccountNamesArray.forEach((accountName) => firstAccountNames.push(accountName));
      parsedAccountNames[0] = firstAccountNames.join(', ');
    }
    return parsedAccountNames;
  }

  getConflictingAccountNames = () => {
    let firstAccountNames = '';
    let lastAccountName = '';
    if(this.props.conflictingAccountNames && this.props.conflictingAccountNames.length) {
      lastAccountName = this.props.conflictingAccountNames[this.props.conflictingAccountNames.length - 1];
      firstAccountNames = this.props.conflictingAccountNames.slice(0, -1).join(', ');
    }
    return {firstAccountNames, lastAccountName};
  }

  render() {
    return (
      <AcceptInvitation
        invitedLicenseCount={this.props.invitedLicenses.length}
        firstProductNames={this.getFirstProductNames()}
        lastProductName={this.getLastProductName()}
        invitingAccountName={this.props.invitingAccountName}
        managerInAccounts={this.getParsedAccountNames(this.props.managerInAccounts)}
        adminInAccounts={this.getParsedAccountNames(this.props.adminInAccounts)}
        acceptInvitation={this.acceptInvitation}
        showDeclineDialog={this.showDeclineDialog}
        noOfConflictingAccounts={this.props.conflictingAccountNames.length}
        conflictingAccountNames={this.getConflictingAccountNames()}
        conflictingLicenses={this.props.conflictingLicenses}
        invitedLicenses={this.props.invitedLicenses}
        isInvitationValid={this.state.isInvitationValid}
        eaIsLoading={this.props.eaIsLoading}
        invitedUserEmail={this.props.invitedUserEmail}
        logout={this.props.logout}
        redirectToMyAccount={this.redirectToMyAccount}
      />
    );
  }
}

AcceptInvitationContainer.propTypes = {
  eaIsLoading: PropTypes.bool.isRequired,
  uiShowConfirmDialog: PropTypes.func.isRequired,
  setInvitationId: PropTypes.func.isRequired,
  extAdminMeRequestWithInvitationsGet: PropTypes.func.isRequired,
  extAdminInvitationAccept: PropTypes.func.isRequired,
  extAdminInvitationDecline: PropTypes.func.isRequired,
  invitationId: PropTypes.string.isRequired,
  invitingAccountKey: PropTypes.string.isRequired,
  myUserKey: PropTypes.string.isRequired,
  managerInAccounts: PropTypes.array.isRequired,
  adminInAccounts: PropTypes.array.isRequired,
  isInvitationValid: PropTypes.bool.isRequired,
  conflictingAccountNames: PropTypes.array.isRequired,
  conflictingLicenses: PropTypes.array.isRequired,
  invitingAccountName: PropTypes.string.isRequired,
  invitedLicenses: PropTypes.array.isRequired,
  uiShowToastrMessage: PropTypes.func.isRequired,
  uiShowMessageBanner: PropTypes.func.isRequired,
  uiShowMessageBannerToggle: PropTypes.func.isRequired,
  uiMessageBannerVisible: PropTypes.bool.isRequired,
  invitedUserEmail: PropTypes.string.isRequired,
  logout: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => {
  const urlParams = new URLSearchParams(window.location.search);
  const invitationId = urlParams.get('id') || '';
  const conflictingAccountDetails = getConflictingAccountDetails(state);
  return {
    invitationId,
    eaIsLoading: eaIsLoading(state),
    invitation: getInvitingAccount(state) || {},
    invitingAccountKey: getInvitingAccountKey(state) || '',
    myUserKey: getMyUserKey(state) || '',
    conflictingAccountNames: conflictingAccountDetails.conflictingAccountNames || [],
    conflictingLicenses: conflictingAccountDetails.conflictingLicenses || [],
    managerInAccounts: conflictingAccountDetails.managerInAccounts || [],
    adminInAccounts: conflictingAccountDetails.adminInAccounts || [],
    invitingAccountName: getInvitingAccountName(state) || '',
    invitedLicenses: getInvitedLicenses(state, invitationId) || [],
    isInvitationValid: isInvitationValid(state),
    uiMessageBannerVisible: uiMessageBannerVisible(state) || false,
    invitedUserEmail: getMyEmailAddress(state) || '',
    logout: auth.logout.bind(auth),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  uiShowConfirmDialog: (textResourceId, yesCallbackFn, noCallbackFn, useCountDownTimer = false, dialogParams = {}) =>
    dispatch(uiShowConfirmDialog(textResourceId, yesCallbackFn, noCallbackFn, useCountDownTimer, dialogParams)),
  setInvitationId: (invitationId) => dispatch(setInvitationId(invitationId)),
  extAdminMeRequestWithInvitationsGet: () => dispatch(extAdminMeRequestWithInvitationsGet()),
  extAdminInvitationAccept: (invitationId) => dispatch(extAdminInvitationAccept(invitationId)),
  extAdminInvitationDecline: (invitationId) => dispatch(extAdminInvitationDecline(invitationId)),
  uiShowToastrMessage: (toastrType, toastrMessageId, toastrParams) => dispatch(uiShowToastrMessage(toastrType, toastrMessageId, toastrParams)),
  uiShowMessageBanner: (messageId, params = {}) => dispatch(uiShowMessageBanner(messageId, params)),
  uiShowMessageBannerToggle: () => dispatch(uiShowMessageBannerToggle()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AcceptInvitationContainer);
